import type { UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import Entity from "_framework/entities/entity";
import Guid from "_shared/guids/guid";
import BookmarkedIndividual from "marketplace/bookmarks/individual-bookmarks/bookmarked-individual";
import BookmarkedIndividualListItemSchema, {
  BookmarkedIndividualListItemJSON,
  BookmarkedIndividualListItemParams,
} from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-list-item-schema";
import Individual from "marketplace/individuals/individual";

/**
 * One row of the caller's individual bookmarks: the bookmark, and the bookmarked person when the
 * caller may see them. A row without the person is still a bookmark that the caller can delete.
 */
export default class BookmarkedIndividualListItem
  extends Entity<BookmarkedIndividualListItem, BookmarkedIndividualListItemJSON, BookmarkedIndividualListItemParams> {
  public static create(values: BookmarkedIndividualListItemParams): BookmarkedIndividualListItem {
    const parsed = BookmarkedIndividualListItemSchema.validate(values);
    return new BookmarkedIndividualListItem(parsed);
  }

  public static fromJSON(json: BookmarkedIndividualListItemJSON): BookmarkedIndividualListItem {
    const parsed = BookmarkedIndividualListItemSchema.validateJSON(json);
    return new BookmarkedIndividualListItem({
      bookmark: BookmarkedIndividual.fromJSON({
        id: parsed.id,
        createdDate: parsed.createdDate,
        individualId: parsed.individualId,
      }),
      individual: Individual.fromJSON(parsed.individual) ?? undefined,
      unavailableReason: parsed.unavailableReason ?? undefined,
    });
  }

  public static fromJSONArray(jsonArray: BookmarkedIndividualListItemJSON[]): BookmarkedIndividualListItem[] {
    return jsonArray.map((json) => BookmarkedIndividualListItem.fromJSON(json));
  }

  public readonly bookmark: BookmarkedIndividual;
  public readonly individual?: Individual;
  public readonly unavailableReason?: Extract<UnavailableReason, "unauthorized">;

  /** The bookmark's id, so a page of rows can be keyed like any other page. */
  public get id(): Guid {
    return this.bookmark.id;
  }

  public get individualId(): Guid {
    return this.bookmark.individualId;
  }

  /** True when the caller may not see the bookmarked person. */
  public get isUnavailable(): boolean {
    return this.unavailableReason !== undefined;
  }

  public constructor(params: BookmarkedIndividualListItemParams) {
    super(params);
    this.bookmark = params.bookmark;
    this.individual = params.individual;
    this.unavailableReason = params.unavailableReason;
  }

  public toJSON(): BookmarkedIndividualListItemJSON {
    return {
      ...this.bookmark.toJSON(),
      individual: this.individual?.toJSON() ?? null,
      unavailableReason: this.unavailableReason ?? null,
    };
  }

  public valueOf(): BookmarkedIndividualListItemJSON {
    return this.toJSON();
  }

  public toString(): string {
    return `Bookmarked Individual List Item: ${this.bookmark.id.toString()} (${this.unavailableReason ?? "visible"})`;
  }

  public isEqualTo(other: BookmarkedIndividualListItem | null | undefined): boolean {
    return Boolean(
      other &&
      this.bookmark.isEqualTo(other.bookmark) &&
      this.unavailableReason === other.unavailableReason &&
      (this.individual ? this.individual.isEqualTo(other.individual) : !other.individual),
    );
  }
}
